/**
 * Pure navigation & questionnaire helpers for Variant B.
 * Extracted from QuestionnaireVariantB.tsx so they can be unit-tested
 * independently of React component lifecycle.
 */

import { findSelectedChild } from "./healthSummaryHelpers";
import { GENDER_VALUES } from "./constants";

// ---------- types ----------

export type PageDescriptor = {
  questionIdx: number;
  subIdx: number | null; // null = main question; number = index in question.additional
};

export type SpecialPageType = "blocked-contraindication" | null;

export type ScreenDescriptor =
  | { type: "page"; page: PageDescriptor }
  | { type: "special"; special: SpecialPageType }
  | { type: "weightProjection" }
  | { type: "realResults"; gender?: Gender }
  | { type: "socialProof" }
  | { type: "healthSummary" }
  // HL-11800 guest-only email capture. `next` is the screen Continue/Skip
  // advance to: the Legal Consent page.
  | { type: "contactDetails"; next: ScreenDescriptor };

export interface ConsentBlock {
  anchorIdx: number | null;
  statementsIdx: number | null;
  fullLegalIdx: number | null;
  skipIdxs: number[];
  allIdxs: number[];
}

// ---------- helpers ----------

export function pageKey(pd: PageDescriptor): string {
  return pd.subIdx === null ? `q${pd.questionIdx}` : `q${pd.questionIdx}s${pd.subIdx}`;
}

/**
 * `question.additional` for a bmi_question erroneously duplicates the weight
 * node (height's only answersGroup child) — it's already rendered inline by
 * BMISliderSection, so exclude it here to stop it being paginated again as
 * its own page. Match by recipe.id.
 */
export function getPaginatableAdditional(question: any): any[] {
  const additional = question.additional || [];
  if (question.question_type !== "bmi_question") return additional;
  const weightAnswerId = question.answersGroup?.children?.[0]?.recipe?.id;
  return additional.filter((subq: any) => subq.recipe?.id !== weightAnswerId);
}

/** Find the next triggered subquestion index after `fromSubIdx` for a question. */
export function nextSubIdx(
  question: any,
  fromSubIdx: number | null,
  answersSelectedMap: any
): number | null {
  const additional = getPaginatableAdditional(question);
  if (!additional.length) return null;
  const startIdx = fromSubIdx === null ? 0 : fromSubIdx + 1;
  for (let i = startIdx; i < additional.length; i++) {
    const subq = additional[i];
    if (subq.recipe.parent_id && answersSelectedMap[subq.recipe.parent_id]) {
      return i;
    }
  }
  return null;
}

/**
 * Question indexes that must be skipped for the current auth state.
 * health_condition is only for signed-in returning patients (skip-to-treatment
 * needs a prior order); guests never see it. Signed-in patients the server
 * found not skip-eligible (`skipEligible === false`) don't see it either: its
 * "nothing changed" answer could only fall through to the next question.
 * `null` = eligibility not known yet, keep it.
 */
export function getAuthGatedSkipIdxs(
  flattenedQuestionnaire: any[],
  isLoggedIn: boolean,
  skipEligible: boolean | null = null
): number[] {
  if (isLoggedIn && skipEligible !== false) return [];
  const idxs: number[] = [];
  for (let i = 0; i < flattenedQuestionnaire.length; i++) {
    if (flattenedQuestionnaire[i]?.question_type === "health_condition") {
      idxs.push(i);
    }
  }
  return idxs;
}

/** Social proof follows the reason question plus the conditional follow-ups its answers reveal. */
export function isWeightLossReasonBlock(flattenedQuestionnaire: any[], idx: number): boolean {
  const question = flattenedQuestionnaire[idx];
  if (question?.question_type === "weight_loss_reason") return true;
  if (question?.conditional_answer_id == null) return false;
  const reason = flattenedQuestionnaire.find((q) => q?.question_type === "weight_loss_reason");
  return (reason?.answersGroup?.children || []).some(
    (child: any) => String(child.recipe?.id) === String(question.conditional_answer_id)
  );
}

/** Compute next page descriptor after the given page. Returns null at end of questionnaire. */
export function computeNextPage(
  current: PageDescriptor,
  flattenedQuestionnaire: any[],
  answersSelectedMap: any,
  skipIdxs: number[] = []
): PageDescriptor | null {
  const question = flattenedQuestionnaire[current.questionIdx];
  if (!question) return null;

  const nextSub = nextSubIdx(question, current.subIdx, answersSelectedMap);
  if (nextSub !== null) {
    return { questionIdx: current.questionIdx, subIdx: nextSub };
  }

  let nextQ = current.questionIdx + 1;
  while (nextQ < flattenedQuestionnaire.length) {
    const candidate = flattenedQuestionnaire[nextQ];
    if (candidate?.question_type === "title") { nextQ++; continue; }
    if (skipIdxs.includes(nextQ)) { nextQ++; continue; }
    if (candidate?.conditional_answer_id && !answersSelectedMap[candidate.conditional_answer_id]) { nextQ++; continue; }
    break;
  }
  if (nextQ < flattenedQuestionnaire.length) {
    return { questionIdx: nextQ, subIdx: null };
  }

  return null;
}

/** Lift a computeNextPage result to a screen: end of questionnaire → Health Summary. */
export function toScreen(next: PageDescriptor | null): ScreenDescriptor {
  return next === null ? { type: "healthSummary" } : { type: "page", page: next };
}

/**
 * HL-11800: when the next screen is the Legal Consent anchor page, put the
 * email-capture screen in front of it, carrying the consent page as `next`.
 * Pure pass-through otherwise (disabled, no consent block, sub-question, or a
 * non-page screen), so every forward-navigation call site can run through it.
 */
export function gateEmailStepBeforeConsent(
  screen: ScreenDescriptor,
  opts: { enabled: boolean; legalConsentIdx: number | null }
): ScreenDescriptor {
  if (!opts.enabled || opts.legalConsentIdx === null) return screen;
  if (screen.type !== "page") return screen;
  if (screen.page.questionIdx !== opts.legalConsentIdx || screen.page.subIdx !== null) return screen;
  return { type: "contactDetails", next: screen };
}

/**
 * HL-11800: rebuild the tail of a resumed stack after the Google redirect that
 * started on the email step. The resumed stack normally ends on the Legal
 * Consent page (the step's anchor):
 *   - guest (cancelled / came back unsigned): wrap it back into the email step;
 *   - signed in: the step is for guests only, so land on its continuation, the
 *     consent page itself. Same outcome as signing in through the step.
 * Anything else (no consent block, stack not on the consent page) is returned
 * untouched so the caller falls back to the normal resume.
 */
export function restoreEmailStepAfterOAuth(
  history: ScreenDescriptor[],
  opts: { loggedIn: boolean; legalConsentIdx: number | null }
): ScreenDescriptor[] {
  if (opts.legalConsentIdx === null || history.length === 0) return history;
  const last = history[history.length - 1];

  // The plain-reload re-insert may already have wrapped the consent page.
  if (last.type === "contactDetails") {
    return opts.loggedIn ? [...history.slice(0, -1), last.next] : history;
  }
  if (last.type !== "page") return history;
  if (last.page.questionIdx !== opts.legalConsentIdx || last.page.subIdx !== null) return history;

  return opts.loggedIn ? history : [...history.slice(0, -1), { type: "contactDetails", next: last }];
}

/**
 * Count remaining reachable pages from `currentPage` to the end of the
 * questionnaire, given the answers selected so far. A live estimate, not a
 * fixed total — later answers can reveal/hide branches.
 */
export function estimateRemainingPages(
  currentPage: PageDescriptor,
  flattenedQuestionnaire: any[],
  answersSelectedMap: any,
  skipIdxs: number[]
): number {
  let count = 0;
  let page: PageDescriptor | null = currentPage;
  while (page) {
    const next = computeNextPage(page, flattenedQuestionnaire, answersSelectedMap, skipIdxs);
    if (next === null) break;
    count++;
    page = next;
  }
  return count;
}

/**
 * Identify consent questions by `question.question_type` (set directly on
 * the recipe_questions row in the DB) — not by
 * guessing from HTML content. All are folded onto one merged "Legal Consent"
 * page:
 *  - statementsIdx: question_type "consent_statements" → inline checks.
 *  - fullLegalIdx: question_type "consent_full_legal" → "view full legal
 *    consent" accordion.
 *
 * When no question has a matching question_type, returns empty — each question
 * renders as a regular scrollable page instead, so the user can always
 * proceed.
 */
export function findConsentBlock(flattenedQuestionnaire: any[]): ConsentBlock {
  const empty: ConsentBlock = {
    anchorIdx: null, statementsIdx: null, fullLegalIdx: null,
    skipIdxs: [], allIdxs: [],
  };

  let statementsIdx: number | null = null;
  let fullLegalIdx: number | null = null;

  for (let i = 0; i < flattenedQuestionnaire.length; i++) {
    const q = flattenedQuestionnaire[i];
    switch (q?.question_type) {
      case "consent_statements": if (statementsIdx === null) statementsIdx = i; break;
      case "consent_full_legal": if (fullLegalIdx === null) fullLegalIdx = i; break;
    }
  }

  const allIdxs = [statementsIdx, fullLegalIdx]
    .filter((v): v is number => v !== null)
    .sort((a, b) => a - b);
  if (allIdxs.length === 0) return empty;

  const anchorIdx = allIdxs[0];
  const skipIdxs = allIdxs.filter((i) => i !== anchorIdx);
  return { anchorIdx, statementsIdx, fullLegalIdx, skipIdxs, allIdxs };
}

/**
 * HTML for the Legal Consent accordion of a consent_full_legal question.
 * DE keeps the legal text on the answer option (its value) — same fallback order as
 * getPageContent subquestion text. The UK answer is only a bare "Yes" and the legal text is the
 * question itself (shared with the one-page questionnaires), so it passes isLegalTextOnQuestion.
 */
export function getConsentAnswerHtml(question: any, isLegalTextOnQuestion = false): string {
  const recipe = question?.answersGroup?.children?.[0]?.recipe;
  const answerHtml = String(recipe?.long_label || recipe?.label || recipe?.value || "");
  return isLegalTextOnQuestion ? String(question?.text || answerHtml) : answerHtml;
}

/**
 * First answerable page — skips leading "title" section-header pages and any
 * indexes in `skipIdxs` (consent fold-ins, auth-gated questions, etc.).
 */
export function firstAnswerablePage(
  flattenedQuestionnaire: any[],
  skipIdxs: number[] = []
): PageDescriptor {
  for (let i = 0; i < flattenedQuestionnaire.length; i++) {
    if (flattenedQuestionnaire[i]?.question_type === "title") continue;
    if (skipIdxs.includes(i)) continue;
    return { questionIdx: i, subIdx: null };
  }
  return { questionIdx: 0, subIdx: null };
}

/**
 * Whether a page already has a selected answer in `answersSelectedMap`.
 * Same answer-shape checks as Variant B's validateCurrentPage, minus the
 * side-effect of writing into the validation object.
 */
export function isPageAnswered(
  page: PageDescriptor,
  flattenedQuestionnaire: any[],
  answersSelectedMap: any
): boolean {
  const pageContent = getPageContent(page, flattenedQuestionnaire);
  if (!pageContent) return true;
  const { answerGroup, questionType } = pageContent;
  if (!answerGroup?.recipe?.id) return true;
  if (questionType === "title") return true;

  if (questionType === "bmi_question") {
    const heightId = answerGroup.recipe.id;
    const weightId = answerGroup.children?.[0]?.recipe?.id;
    return !!(answersSelectedMap[heightId] && weightId && answersSelectedMap[weightId]);
  }
  if (answerGroup.recipe.type.includes("check_list")) {
    return !!answerGroup.children?.some((child: any) => answersSelectedMap[child.recipe.id]);
  }
  if (["number", "textarea", "text", "date"].includes(answerGroup.recipe.type)) {
    return !!answersSelectedMap[answerGroup.recipe.id];
  }
  return !!answerGroup.children?.some((child: any) => answersSelectedMap[child.recipe.id]);
}

/**
 * Path from the first answerable page through every answered page, ending
 * at the first unanswered one (or the last reachable page if all are
 * answered). Used on restore so Back can walk the same stack the user
 * originally built, instead of landing on a 1-entry history.
 */
export function resumePageHistory(
  flattenedQuestionnaire: any[],
  answersSelectedMap: any,
  skipIdxs: number[] = []
): PageDescriptor[] {
  const path: PageDescriptor[] = [];
  let page: PageDescriptor | null = firstAnswerablePage(flattenedQuestionnaire, skipIdxs);
  while (page) {
    path.push(page);
    if (!isPageAnswered(page, flattenedQuestionnaire, answersSelectedMap)) break;
    page = computeNextPage(page, flattenedQuestionnaire, answersSelectedMap, skipIdxs);
  }
  return path.length > 0 ? path : [firstAnswerablePage(flattenedQuestionnaire, skipIdxs)];
}

/** First unanswered reachable page after restore — last entry of resumePageHistory. */
export function firstUnansweredPage(
  flattenedQuestionnaire: any[],
  answersSelectedMap: any,
  skipIdxs: number[] = []
): PageDescriptor {
  const path = resumePageHistory(flattenedQuestionnaire, answersSelectedMap, skipIdxs);
  return path[path.length - 1];
}

/**
 * Compute revealedAnswerGroups for submission from answersSelectedMap — no
 * dependency on component mount/unmount lifecycle.
 */
export function computeRevealedForSubmit(
  flattenedQuestionnaire: any[],
  answersSelectedMap: any,
  skipIdxs: number[] = []
): Record<string | number, boolean> {
  const revealed: Record<string | number, boolean> = {};
  flattenedQuestionnaire.forEach((question, idx) => {
    if (skipIdxs.includes(idx)) return;
    if (question.conditional_answer_id && !answersSelectedMap[question.conditional_answer_id]) return;
    if (question.answersGroup?.recipe?.id) {
      revealed[question.answersGroup.recipe.id] = true;
    }
    for (const subq of getPaginatableAdditional(question)) {
      if (subq.recipe.parent_id && answersSelectedMap[subq.recipe.parent_id]) {
        revealed[subq.recipe.id] = true;
      }
    }
  });
  return revealed;
}

/** Get the question text and answer group for a page descriptor. */
export function getPageContent(pd: PageDescriptor, flattenedQuestionnaire: any[]) {
  const question = flattenedQuestionnaire[pd.questionIdx];
  if (!question) return null;
  if (pd.subIdx === null) {
    return {
      text: question.text,
      helpBlock: question.help_block,
      whyWeAskBlock: question.answersGroup?.recipe?.why_we_ask_block,
      answerGroup: question.answersGroup,
      questionType: question.question_type,
      question,
    };
  }
  const subq = getPaginatableAdditional(question)[pd.subIdx];
  if (!subq) return null;
  return {
    text: subq.recipe.long_label || subq.recipe.label || subq.recipe.value,
    helpBlock: subq.recipe.help_block,
    whyWeAskBlock: subq.recipe.why_we_ask_block,
    answerGroup: subq,
    questionType: "subquestion_" + subq.recipe.type,
    question,
  };
}

/**
 * HL-11195: condition-name orange highlight disabled for now — keep follow-up
 * question copy plain black. Hook kept so call sites stay stable; restore the
 * full-phrase highlighter here when product wants orange again.
 */
export function highlightRevealingDiseaseName(html: string | undefined, _pageContent: any): string | undefined {
  return html;
}

export type Gender = keyof typeof GENDER_VALUES;

// Allow-list for detecting the biological-gender question's answers.
export function genderOfAnswer(value: any): Gender | null {
  const v = String(value).trim().toLowerCase();
  if ((GENDER_VALUES.male as readonly string[]).includes(v)) return "male";
  if ((GENDER_VALUES.female as readonly string[]).includes(v)) return "female";
  return null;
}

const YES_ANSWER_VALUES = new Set(["yes", "ja", "oui"]);

export function isYesAnswer(value: any): boolean {
  return YES_ANSWER_VALUES.has(String(value).trim().toLowerCase());
}

/**
 * health_condition "something changed" path (Yes / Ja / Oui), including longer
 * CMS labels like "Yes, something has changed".
 */
export function isHealthConditionSomethingChanged(value: any): boolean {
  const v = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/<[^>]+>/g, "");
  if (!v) return false;
  if (YES_ANSWER_VALUES.has(v)) return true;
  return /^(yes|ja|oui)([\s,]|$)/i.test(v);
}

/** health_condition "nothing changed" path → skip to treatment selection. */
export function isHealthConditionNothingChanged(value: any): boolean {
  return !isHealthConditionSomethingChanged(value);
}

/**
 * Q1 phrased as "Is this your first time ordering from us?" (WL3): there the
 * returning patient answers "No". Every other Q1 ("Have you taken GLP-1
 * before?", WL2) means "Yes" is the returning patient.
 */
const FIRST_TIME_QUESTION_RE = /first time|erste[sn]? mal|premi[eè]re fois/i;

/** Whether Q1 (the first question shown) is answered as a returning patient. */
export function isQ1ReturningPatient(
  flattenedQuestionnaire: any[],
  pageHistory: PageDescriptor[],
  answersSelectedMap: any
): boolean {
  const q1 = flattenedQuestionnaire[pageHistory[0]?.questionIdx];
  const selectedChild = findSelectedChild(q1?.answersGroup?.children, answersSelectedMap);
  if (!selectedChild) return false;
  const answeredYes = isYesAnswer(selectedChild.recipe.value);
  const asksFirstTime = FIRST_TIME_QUESTION_RE.test(String(q1?.text ?? "").replace(/<[^>]+>/g, " "));
  return asksFirstTime ? !answeredYes : answeredYes;
}

/** Check if any currently selected answer on a page has a contraindication significance. */
export function hasContraindication(pageContent: any, answersSelectedMap: any): boolean {
  const ag = pageContent?.answerGroup;
  if (!ag?.children) return false;
  return ag.children.some(
    (child: any) =>
      answersSelectedMap[child.recipe.id] &&
      child.recipe.significances?.some(
        (sig: any) => sig.evaluation_type === "contraindications"
      )
  );
}

/**
 * Pregnancy / trying-to-conceive / breastfeeding follow-up (shown after female
 * gender). Matched by question copy so the Yes → blocker path still works if
 * the answer is missing a contraindications significance in CMS.
 */
const PREGNANCY_QUESTION_RE =
  /pregnant|pregnancy|breastfeed|conceive|schwanger|stillend|stillen|schwangerschaft|enceinte|allait/i;

export function isPregnancyQuestion(pageContent: any): boolean {
  const text = String(pageContent?.text || "")
    .replace(/<[^>]+>/g, " ")
    .trim();
  return PREGNANCY_QUESTION_RE.test(text);
}

/** True when the pregnancy follow-up is answered Yes / Ja / Oui. */
export function isPregnancyYesSelected(pageContent: any, answersSelectedMap: any): boolean {
  if (!isPregnancyQuestion(pageContent)) return false;
  const selected = findSelectedChild(pageContent?.answerGroup?.children, answersSelectedMap);
  return !!selected && isYesAnswer(selected.recipe?.value);
}

/**
 * Hard-stop answers for Variant B blocker modal: CMS contraindications OR
 * pregnancy Yes (even without a significance row).
 */
export function isBlockerAnswer(pageContent: any, answersSelectedMap: any): boolean {
  return (
    hasContraindication(pageContent, answersSelectedMap) ||
    isPregnancyYesSelected(pageContent, answersSelectedMap)
  );
}

/** Weight-related illness question for the BMI 27–29.9 rule — marked in DB via validation_rule is_bmi_comorbidity. */
export function isBmiComorbidityQuestion(pageContent: any): boolean {
  return !!pageContent?.answerGroup?.recipe?.validation_rule?.is_bmi_comorbidity;
}

/** An illness is picked: radio → Yes / Ja / Oui; check list → any option except is_exclusive_none. */
export function hasSelectedComorbidity(answerGroup: any, answersSelectedMap: any): boolean {
  const selected = (answerGroup?.children || []).filter((child: any) => answersSelectedMap[child.recipe.id]);
  if (answerGroup?.recipe?.type === "radio_buttons") {
    return selected.some((child: any) => isYesAnswer(child.recipe.value));
  }
  return selected.some((child: any) => !child.recipe.validation_rule?.is_exclusive_none);
}